import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTheme } from "../context/ThemeContext";
import { resolveLogoUrl, DEFAULT_THEME } from "../Hooks/useTenantTheme";
import { getTenantSubdomain } from "../Hooks/useTenantFromSubdomain";
import { BRAND_COLOR_FIELDS, FONT_COLOR_FIELDS, ColorField, SectionCard } from "../Components/ThemeColorFields";
import LogoUploader from "../Components/LogoUploader";

const { REACT_APP_BASE_URL } = process.env;

const ThemeSettings = () => {
  const themeContext = useTheme();
  const [form, setForm] = useState(() => ({ ...themeContext?.theme }));
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(resolveLogoUrl(themeContext?.theme?.logoUrl));
  const [isSaving, setIsSaving] = useState(false);
  const isDirtyRef = useRef(false);

  // Safety net: ThemeContext's own initial-mount fetch only ever runs once,
  // before login (no token yet, so it no-ops) — this page is where a stale/
  // default theme would be most visibly wrong, so refetch on mount here too,
  // in case some login path was missed.
  useEffect(() => {
    themeContext?.fetchTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // form was only seeded once from whatever theme existed at mount time —
  // sync it once the refetch above actually resolves with real server data.
  useEffect(() => {
    if (isDirtyRef.current) return;
    setForm((prev) => ({ ...prev, ...themeContext?.theme }));
    setLogoPreview(resolveLogoUrl(themeContext?.theme?.logoUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeContext?.theme]);

  const handleColorChange = (key, value) => {
    isDirtyRef.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleLogoSelect = (file) => {
    isDirtyRef.current = true;
    setLogoFile(file);
    setLogoPreview((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleLogoRemove = () => {
    isDirtyRef.current = true;
    setLogoFile(null);
    setLogoPreview(null);
    setForm((prev) => ({ ...prev, logoUrl: null }));
  };

  const handleResetToDefault = () => {
    isDirtyRef.current = true;
    setForm({ ...DEFAULT_THEME });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const authHeaders = () => {
    const subdomain = getTenantSubdomain();
    return {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      ...(subdomain ? { "X-Tenant-Subdomain": subdomain } : {}),
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let logoUrl = form.logoUrl;

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        const uploadRes = await axios.post(
          `${REACT_APP_BASE_URL}/tenant/theme/logo`,
          formData,
          { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
        );
        logoUrl = uploadRes.data.logoUrl;
      }

      const payload = { ...form, logoUrl };
      const updateRes = await axios.put(`${REACT_APP_BASE_URL}/tenant/theme`, payload, {
        headers: authHeaders(),
      });

      themeContext?.updateThemeLocally(updateRes.data);
      setForm(updateRes.data);
      setLogoFile(null);
      toast.success("Theme updated successfully!");
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update theme");
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-canvas-userbar w-full px-6 py-4">
      <div className="bg-canvas-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        <div
          className="flex flex-col p-5 gap-1 shrink-0"
          style={{
            background: `linear-gradient(135deg, ${form.primaryColor || "#2a3472"}, ${form.secondaryColor || "#898C9C"})`,
          }}
        >
          <h2 className="!text-canvas-white">Theme Settings</h2>
          <h6 className="!text-canvas-white text-opacity-85 font-normal">
            Customize your organization's brand colors, logo, and app name.
            These changes apply to everyone in your organization.
          </h6>
        </div>

        <div className="flex flex-col divide-y divide-canvas-tersery/20">
          <SectionCard
            title="Brand Identity"
            description="Your organization's name and logo, shown throughout the app."
          >
            <div className="flex flex-wrap items-start gap-8">
              <div className="flex flex-col gap-1 order-2 sm:order-1">
                <h6 className="text-canvas-directoryText">App Name</h6>
                <input
                  type="text"
                  className="border border-canvas-tersery rounded-lg p-2 w-full min-w-[260px] max-w-[400px] focus:outline-none focus:border-canvas-primary focus:ring-2 focus:ring-canvas-primary/20 transition-shadow"
                  value={form.appName || ""}
                  onChange={(e) => {
                    isDirtyRef.current = true;
                    setForm((prev) => ({ ...prev, appName: e.target.value }));
                  }}
                />
              </div>

              <div className="flex flex-col gap-1 order-1 sm:order-2">
                <h6 className="text-canvas-directoryText">Logo</h6>
                <LogoUploader
                  preview={logoPreview}
                  fileName={logoFile?.name}
                  onSelect={handleLogoSelect}
                  onRemove={handleLogoRemove}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Brand Colors"
            description="Applied to the sidebar, buttons, highlights, and text across the app."
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {(() => {
                const fields = [...BRAND_COLOR_FIELDS, ...FONT_COLOR_FIELDS];
                return fields.map(({ key, label }, index) => (
                  <ColorField
                    key={key}
                    label={label}
                    value={form[key]}
                    onChange={(value) => handleColorChange(key, value)}
                    align={index >= fields.length - 2 ? "right" : "left"}
                  />
                ));
              })()}
            </div>
          </SectionCard>
        </div>

        <div className="flex items-center gap-4 px-5 py-4 bg-canvas-userbar/40 shrink-0">
          <button
            type="button"
            className="font-outfit text-[12px] uppercase font-bold bg-canvas-primary text-canvas-white py-2.5 px-7 rounded-md shadow-md hover:shadow-lg hover:brightness-110 transition-all disabled:cursor-not-allowed disabled:bg-canvas-primary/20 disabled:text-canvas-primary disabled:shadow-none"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Theme"}
          </button>
          <button
            type="button"
            className="font-outfit text-[12px] uppercase font-bold border border-canvas-primary text-canvas-primary py-2.5 px-7 rounded-md hover:bg-canvas-primary/5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleResetToDefault}
            disabled={isSaving}
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeSettings;
